import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth, useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import type { Note } from '@verantos-dev/core-annotation-tool';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';
import type { DropdownListItem } from '@verantos-dev/shared-components';

import { useGetSupervisorWorkspaceQuery } from '../../api/supervisorApiSlice';
import { useSupervisorMode } from '../../context/SupervisorModeContext';
import { selectFilterBy, selectNoteSortBy } from '../../state/selector';
import { clearFilterBy } from '../../state/supervisorSlice';

export interface UseNoteListSidebarSupervisorResult {
  notes: Note[];
  isSupervisorWorkspaceLoading: boolean;
  filterOptions: DropdownListItem[];
}

export const useNoteListSidebarSupervisor = (): UseNoteListSidebarSupervisorResult => {
  const dispatch = useAppDispatch();
  const filterBy = useAppSelector(selectFilterBy);
  const noteSortBy = useAppSelector(selectNoteSortBy);
  const { encounterId } = useParams();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  const batchId = useAppSelector(selectBatchId);

  const {
    data: supervisorWorkspaceData,
    isLoading: isSupervisorWorkspaceLoading,
    isFetching: isSupervisorWorkspaceFetching,
  } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );

  const notes = supervisorWorkspaceData?.data?.notes ?? [];

  const filterOptions: DropdownListItem[] = useMemo(() => {
    const uniqueNoteTypes = [...new Set(notes.map((note) => note.noteType))];

    const noteTypeOptions = uniqueNoteTypes.map((noteType) => ({
      value: noteType,
      displayValue: noteType,
    }));

    // Add 'All' option at the beginning and keep it selected by default
    return [{ value: 'all', displayValue: 'All' }, ...noteTypeOptions];
  }, [notes]);

  useEffect(() => {
    dispatch(clearFilterBy());
  }, [filterOptions, dispatch]);

  const filteredNotes = useMemo(() => {
    if (filterBy === 'all') {
      return notes;
    }
    return notes.filter((note) => note.noteType === filterBy);
  }, [notes, filterBy]);

  const sortedNotes = useMemo(() => {
    const notesCopy = [...filteredNotes];

    if (noteSortBy === 'ascending') {
      return notesCopy.sort((a, b) => a.noteDatetime.localeCompare(b.noteDatetime));
    }

    return notesCopy.sort((a, b) => b.noteDatetime.localeCompare(a.noteDatetime));
  }, [filteredNotes, noteSortBy]);

  return {
    notes: sortedNotes,
    isSupervisorWorkspaceLoading: isSupervisorWorkspaceLoading || isSupervisorWorkspaceFetching,
    filterOptions,
  };
};
